import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// During Phase 1 the .env may not be populated. Avoid hard-failing render —
// instead surface a console warning so the marketing site still loads.
const configured = !!cfg.apiKey && !!cfg.projectId;
if (!configured && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.warn(
    '[AnySharps] Firebase env vars are not set. Auth + Firestore features will be disabled. ' +
      'Copy apps/web/.env.example to apps/web/.env.local and fill in values.',
  );
}

let app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _functions: Functions | null = null;

function ensureApp(): FirebaseApp {
  if (!app) {
    if (!configured) throw new Error('Firebase is not configured. See apps/web/.env.example.');
    app = initializeApp(cfg);
  }
  return app;
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    _auth = getAuth(ensureApp());
    if (import.meta.env.VITE_USE_EMULATORS === '1') {
      connectAuthEmulator(_auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    }
  }
  return _auth;
}

export function getDb(): Firestore {
  if (!_db) {
    // initializeFirestore (not getFirestore) so we can pass settings on first
    // creation. ignoreUndefinedProperties matches the server-side default we
    // set in apps/functions/src/index.ts — keeps writes from crashing when
    // optional fields haven't been filled yet.
    _db = initializeFirestore(ensureApp(), { ignoreUndefinedProperties: true });
    if (import.meta.env.VITE_USE_EMULATORS === '1') {
      connectFirestoreEmulator(_db, '127.0.0.1', 8080);
    }
  }
  return _db;
}

export function getStorageRef(): FirebaseStorage {
  if (!_storage) {
    _storage = getStorage(ensureApp());
    if (import.meta.env.VITE_USE_EMULATORS === '1') {
      connectStorageEmulator(_storage, '127.0.0.1', 9199);
    }
  }
  return _storage;
}

export function getFn(): Functions {
  if (!_functions) {
    _functions = getFunctions(ensureApp(), 'us-central1');
    if (import.meta.env.VITE_USE_EMULATORS === '1') {
      connectFunctionsEmulator(_functions, '127.0.0.1', 5001);
    }
  }
  return _functions;
}

export const isFirebaseConfigured = () => configured;
